// @flow
import * as React from 'react';

/**
 * Converts a value in `rem` units to pixels,
 * based on the root `<html>` font size.
 */
const remToPx = (rem: number): number => {
  const DEFAULT_FONT_SIZE = 16;
  // In SSR or test environments, `window` may not exist.
  if (typeof window === 'undefined') return rem * DEFAULT_FONT_SIZE;

  const rootFontSize = parseFloat(
    window.getComputedStyle(document.documentElement).fontSize,
  );

  // Fallback to 16px if the computed font size cannot be determined.
  if (Number.isNaN(rootFontSize)) return rem * DEFAULT_FONT_SIZE;
  return rem * rootFontSize;
};

/**
 * Generic scroll-spy hook.
 *
 * - `sectionIds`: Array of DOM `id` strings representing sections.
 * - `offsetRem`: Vertical offset from the top of the viewport (in rem)
 *   where the tracking “activation line” is located (e.g. 20rem).
 *
 * A section is considered *active* when:
 *   - The **top** of the section is above or aligned with the activation line.
 *   - AND the **bottom** of the section is below or aligned with the activation line.
 *
 * → The Tag becomes active when the top of the section reaches the activation line,
 *   and becomes inactive once the bottom of the section reaches the activation line.
 */
export function useScrollSpy<T: string>(
  sectionIds: $ReadOnlyArray<T>,
  offsetRem: number = 20,
): ?T {
  // Stores the currently active section id, or `null` if none match
  const [activeId, setActiveId] = React.useState<?T>(null);

  React.useEffect(() => {
    // Avoid running in SSR environments
    if (typeof window === 'undefined') return undefined;

    // Convert the offset (in rem) to pixels
    const offsetPx = remToPx(offsetRem);

    const handleScroll = () => {
      let currentId: ?T = null;

      // Iterate through section ids in order.
      // The first one matching the activation logic becomes the active section.
      for (const id of sectionIds) {
        const container = document.getElementById(String(id));

        if (container) {
          const rect = container.getBoundingClientRect();
          const sectionTop = rect.top;
          const sectionBottom = rect.bottom;

          const lineY = offsetPx;

          const topPassedLine = sectionTop <= lineY;
          const bottomBelowLine = sectionBottom >= lineY;

          if (topPassedLine && bottomBelowLine) {
            currentId = id;
            break;
          }
        }
      }

      setActiveId(currentId);
    };

    // Run once on mount (handles cases where the page is already scrolled)
    handleScroll();

    // Update on scroll and on window resize
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [sectionIds, offsetRem]);

  // Return the id of the active section, or `null` if none
  return activeId;
}
